/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Form, Row, Space, Tabs, Typography } from 'antd';
import BotDeploymentsGeneralForm from './DeploymentSettings/BotDeploymentsGeneralForm';
import { SlackIcon } from '../Icons/platformIcons/Slack';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { DEFAULT_DATETIME_TIMEZONE_FORMAT, BOT_DEPLOYMENTS_CHANNELS } from '../../constants/main';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import { useDeploymentFormErrors } from '../../utils/hooks/useDeploymentFormErrors';

const BotDeploymentsSlackForm = ({
  formData={},
  onFormSubmit= () => {},
  userRoles={},
  isCreateMode=false,
  isUpdating=false,
  isCreating=false,
  handleBackButton= () => {},
  onDeleteClick = () => {},
  bodyClassName = '',
  footerClassName = '',
  ...restProps
}) => {
  const [form] = Form.useForm();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);
  const { t } = useTranslation();

  const {
    errorFields,
    generateTabLabel,
    markAsErroneous,
    setErrorFields
  } = useDeploymentFormErrors();

  const initialValues = useMemo(() => {
    return isCreateMode ? {} : {
      ...formData
    };
  }, [formData]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  // Form handlers
  const onFormSave = (values) => {
    setErrorFields([]);
    const valuesToSubmit = {
      ...values
    };

    delete valuesToSubmit['hideTypingIndicator'];

    if (isCreateMode) {
      valuesToSubmit['platform'] = BOT_DEPLOYMENTS_CHANNELS.SLACK.key;
      valuesToSubmit['scope'] = 'USER';
    }

    onFormSubmit(valuesToSubmit, formData.id, isCreateMode);
  };

  const onFieldError = (fields) => {
    setErrorFields(fields.errorFields || []);
    if (!isEmptyArray(fields.errorFields)) {
      form.scrollToField(fields.errorFields[0].name, { scrollMode: 'if-needed', behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        <Form
          name="slackDeploymentForm"
          layout="vertical"
          onFinish={onFormSave}
          onFinishFailed={onFieldError}
          onFields
          form={form}
          initialValues={initialValues}
          {...restProps}
        >
          <Typography.Title level={4} className="mb-2">
            <Space>
              <SlackIcon />
              {isCreateMode ? t('Slack Deployment') : `${formData.displayName}`}
            </Space>
          </Typography.Title>

          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: generateTabLabel(t('General Settings'), '1'),
                forceRender: true,
                children: <BotDeploymentsGeneralForm
                  form={form}
                  formData={formData}
                  isCreateMode={isCreateMode}
                  onDeleteClick={onDeleteClick}
                  userRoles={userRoles}
                  description={t('Give a name and description to your deployment to make it easy to recognize. This is only for internal purposes.')}
                  hasErrors={errorFields.length}
                  shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, '1')}
                />
              }
            ]}
          />
        </Form>
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            {
              errorFields.length ? <p className="text-error">The form contains errors. Please review each tab to correct them.</p> : null
            }
          </Col>
        </Row>
        <Space className="d-flex justify-content-between">
          <Typography.Paragraph
            ellipsis
            className={classNames('text-secondary fs-sm mb-0', { 'opacity-0': isCreateMode })}
          >
            {
              !isCreateMode ? (
                <Fragment>
                  <span className="fw-bold">{t('Created')}:</span> {moment(formData.created).tz(formData.tenant.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT) }<br/>
                  <span className="fw-bold">{t('Last Modified')}:</span> {formData.lastChangedBy?.name ? `${formData.lastChangedBy.name}` : null} {moment(formData.lastChanged).tz(formData.tenant.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}<br/>
                </Fragment>
              ) : null
            }
          </Typography.Paragraph>
          <Row justify="end" gutter={8}>
            <Col>
              <Button
                size="large"
                onClick={handleBackButton}
                disabled={isUpdating || isCreating}
              >
                {t('Cancel')}
              </Button>
            </Col>

            <Col>
              <Button
                disabled={isUpdating || isCreating}
                hidden={!hasEditAccess}
                className="float-right"
                type="primary"
                size="large"
                onClick={ form.submit }
              >
                {isCreateMode ? t('Create Deployment') : t('Save Changes')}
              </Button>
            </Col>
          </Row>
        </Space>
      </div>
    </Fragment>
  );
};

BotDeploymentsSlackForm.propTypes = {
  formData: PropTypes.object,
  onFormSubmit: PropTypes.func,
  userRoles: PropTypes.object,
  isCreateMode: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isCreating: PropTypes.bool,
  handleBackButton: PropTypes.func,
  bodyClassName: PropTypes.string,
  onDeleteClick: PropTypes.func,
  footerClassName: PropTypes.string
};

export default BotDeploymentsSlackForm;
